// import { ThemeEvents } from '@theme/events';
// import { Component } from '@theme/component';
// import { StandardEvents, ProductSelectEvent } from '@shopify/events';

// /**
//  * @typedef {Object} ProductPriceRefs
//  * @property {HTMLElement} priceContainer
//  * @property {HTMLElement} [volumePricingNote]
//  */

// /**
//  * A custom element that displays a product price.
//  * This component listens for variant update events and updates the price display accordingly.
//  * It handles price updates from two different sources:
//  * 1. Variant picker (in quick add modal or product page)
//  * 2. Swatches variant picker (in product cards)
//  *
//  * @extends {Component<ProductPriceRefs>}
//  */
// class ProductPrice extends Component {
//   connectedCallback() {
//     super.connectedCallback();
//     const closestSection = this.closest('.shopify-section, dialog');
//     if (!closestSection) return;
//     closestSection.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     const closestSection = this.closest('.shopify-section, dialog');
//     if (!closestSection) return;
//     closestSection.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
//   }

//   /**
//    * Handles product select event and updates the price.
//    * @param {ProductSelectEvent} event - The product select event.
//    */
//   #handleProductSelect = (event) => {
//     if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

//     event.promise
//       .then(({ detail }) => {
//         if (!detail?.html) return;

//         const { html, newProduct } = detail;

//         if (newProduct) {
//           this.dataset.productId = newProduct.id;
//         } else if (detail.productId && detail.productId !== this.dataset.productId) {
//           return;
//         }

//         const { priceContainer, volumePricingNote } = this.refs;
//         // Find the new product-price element in the updated HTML
//         const newProductPrice = html.querySelector(`product-price[data-block-id="${this.dataset.blockId}"]`);
//         if (!newProductPrice) return;

//         // Update price container
//         const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
//         if (newPrice && priceContainer) {
//           priceContainer.replaceWith(newPrice);
//         }

//         // Update volume pricing note
//         const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');
//         if (!newNote) {
//           volumePricingNote?.remove();
//         } else if (!volumePricingNote) {
//           // Use newPrice since priceContainer was just replaced and now points to the detached element
//           newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
//         } else {
//           volumePricingNote.replaceWith(newNote);
//         }

//         // Update installments (SPI banner) variant ID to trigger payment terms re-render
//         const installmentsInput = /** @type {HTMLInputElement|null} */ (
//           this.querySelector(`#product-form-installment-${this.dataset.blockId} input[name="id"]`)
//         );
//         if (installmentsInput) {
//           installmentsInput.value = detail.resource?.id ?? '';
//           installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
//         }
//       })
//       .catch((error) => {
//         if (error?.name !== 'AbortError') console.warn('[product-price] Event promise rejected:', error);
//       });
//   };
// }

// if (!customElements.get('product-price')) {
//   customElements.define('product-price', ProductPrice);
// }













import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class ProductPrice extends Component {
  #requestNumber = 0;
  #section = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section, dialog');

    this.#section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );
  }

  disconnectedCallback() {
    this.#section?.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    super.disconnectedCallback();
  }

  #getVariantData() {
    const script = this.querySelector(
      'script[data-instant-variant-prices]'
    );

    if (!script?.textContent) return null;

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[Instant Price] Invalid JSON', error);
      return null;
    }
  }

  #findSelectedVariant(picker, data) {
    if (
      typeof picker.getAllSelectedOptions !== 'function' ||
      !Array.isArray(data?.variants)
    ) {
      return null;
    }

    const selected = picker.getAllSelectedOptions();
    const values = selected.map((option) => option.value);

    if (!values.length) return null;

    return data.variants.find((variant) => {
      return (
        Array.isArray(variant.options) &&
        variant.options.length === values.length &&
        variant.options.every(
          (value, index) => value === values[index]
        )
      );
    }) || null;
  }

  #updateInstantPrice(picker) {
    const data = this.#getVariantData();

    if (!data) return;

    const variant = this.#findSelectedVariant(
      picker,
      data
    );

    if (!variant || variant.complex) return;

    const container = this.querySelector(
      '[ref="priceContainer"]'
    );

    if (!container) return;

    const fragment = document.createDocumentFragment();

    const createPrice = (
      value,
      className,
      label
    ) => {
      const span = document.createElement('span');
      span.className = className;

      if (label) {
        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = `${label} `;

        span.appendChild(hidden);
      }

      span.appendChild(
        document.createTextNode(value || '')
      );

      return span;
    };

    if (variant.sale) {
      const sale = createPrice(
        variant.price,
        'price-item__group price',
        data.saleLabel
      );

      const compare = createPrice(
        variant.compare,
        'price-item__group price-item--regular compare-at-price',
        data.regularLabel
      );

      if (data.showSaleFirst) {
        fragment.append(sale, compare);
      } else {
        fragment.append(compare, sale);
      }
    } else {
      fragment.appendChild(
        createPrice(variant.price, 'price', '')
      );
    }

    container.replaceChildren(fragment);
  }

  #handleProductSelect = (event) => {
    const picker = event.target;

    if (!(picker instanceof Element)) return;

    if (!picker.matches('variant-picker')) return;

    if (picker.closest('product-card')) return;

    const pickerProductId = picker.dataset.productId;
    const currentProductId = this.dataset.productId;

    if (
      pickerProductId &&
      currentProductId &&
      pickerProductId !== currentProductId
    ) {
      return;
    }

    const requestNumber = ++this.#requestNumber;

    // Update displayed price immediately.
    this.#updateInstantPrice(picker);

    // Keep Horizon's original server synchronization.
    event.promise
      .then(({ detail }) => {
        if (requestNumber !== this.#requestNumber) {
          return;
        }

        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (
          detail.productId &&
          detail.productId !== this.dataset.productId
        ) {
          return;
        }

        const newProductPrice = html.querySelector(
          `product-price[data-block-id="${this.dataset.blockId}"]`
        );

        if (!newProductPrice) return;

        const currentPrice = this.querySelector(
          '[ref="priceContainer"]'
        );

        const newPrice = newProductPrice.querySelector(
          '[ref="priceContainer"]'
        );

        if (currentPrice && newPrice) {
          currentPrice.replaceWith(newPrice);
        }

        // Update variant JSON after server response.
        const currentData = this.querySelector(
          'script[data-instant-variant-prices]'
        );

        const newData = newProductPrice.querySelector(
          'script[data-instant-variant-prices]'
        );

        if (currentData && newData) {
          currentData.textContent = newData.textContent;
        }

        const currentNote = this.querySelector(
          '[ref="volumePricingNote"]'
        );

        const newNote = newProductPrice.querySelector(
          '[ref="volumePricingNote"]'
        );

        if (!newNote) {
          currentNote?.remove();
        } else if (currentNote) {
          currentNote.replaceWith(newNote);
        } else {
          const activePrice = this.querySelector(
            '[ref="priceContainer"]'
          );

          activePrice?.insertAdjacentElement(
            'afterend',
            newNote.cloneNode(true)
          );
        }

        // Keep Shopify payment installments synced.
        const installmentsInput = this.querySelector(
          `#product-form-installment-${this.dataset.blockId} input[name="id"]`
        );

        if (installmentsInput) {
          installmentsInput.value =
            detail.resource?.id ?? '';

          installmentsInput.dispatchEvent(
            new Event('change', { bubbles: true })
          );
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[Product Price] Update failed:',
            error
          );
        }
      });
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
